import React, { useState, useEffect, useMemo } from 'react';
import {
  HiOutlineMagnifyingGlass,
  HiOutlineCheckCircle,
  HiOutlineXMark,
  HiOutlineLockClosed,
  HiOutlineArrowPath,
  HiOutlineUserCircle,
} from 'react-icons/hi2';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../../services';
import { permissionService } from '../../services';
import { userService } from '../../services';
import { toast } from 'react-toastify';
import PageLoading from '../../components/common/PageLoading';
import './PermissionsPage.css';

const PermissionsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tenantId = authService.getTenantId();
  
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [userPermissions, setUserPermissions] = useState({}); // { userId: [permissionIds] }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [localUserPermissions, setLocalUserPermissions] = useState({});

  // Get userId from URL params if present
  useEffect(() => {
    const userIdParam = searchParams.get('userId');
    if (userIdParam) {
      setSelectedUserId(parseInt(userIdParam));
    }
  }, [searchParams]);

  useEffect(() => {
    if (tenantId) {
      const timer = setTimeout(() => {
        loadData(false);
      }, 0);
      return () => clearTimeout(timer);
    } else {
      setError('Please login to view permissions');
      setLoading(false);
    }
  }, [tenantId]);

  const loadData = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError('');
      
      const [usersData, permissionsData] = await Promise.all([
        userService.getAllUsers(),
        permissionService.getAllPermissions(),
      ]);

      // Filter only active users
      const activeUsers = (usersData || []).filter(u => u.status === 'Active');
      setUsers(activeUsers);
      setPermissions(permissionsData || []);

      // Load permissions for all users
      const userPermsMap = {};
      for (const user of activeUsers) {
        try {
          const userPerms = await permissionService.getUserPermissionsFull(user.userId);
          userPermsMap[user.userId] = userPerms.map(p => p.permissionId);
        } catch (err) {
          console.error(`Error loading permissions for user ${user.userId}:`, err);
          userPermsMap[user.userId] = [];
        }
      }
      
      setUserPermissions(userPermsMap);
      setLocalUserPermissions(userPermsMap);
      
      // Auto-select first user if none selected
      if (!selectedUserId && activeUsers && activeUsers.length > 0) {
        setSelectedUserId(activeUsers[0].userId);
      }
    } catch (err) {
      setError('Failed to load permissions. Please try again.');
      console.error('Error loading permissions:', err);
      setUsers([]);
      setPermissions([]);
      setUserPermissions({});
      setLocalUserPermissions({});
    } finally {
      setLoading(false);
    }
  };

  // Group permissions by module
  const permissionsByModule = useMemo(() => {
    const grouped = {};
    permissions.forEach(perm => {
      if (!grouped[perm.module]) {
        grouped[perm.module] = [];
      }
      grouped[perm.module].push(perm);
    });
    return grouped;
  }, [permissions]);

  // Get available modules
  const modules = useMemo(() => {
    return Object.keys(permissionsByModule).sort();
  }, [permissionsByModule]);

  // Filter permissions based on search and module filter
  const filteredPermissions = useMemo(() => {
    let filtered = permissions;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.permissionName?.toLowerCase().includes(term) ||
        p.permissionCode?.toLowerCase().includes(term) ||
        p.module?.toLowerCase().includes(term) ||
        p.action?.toLowerCase().includes(term)
      );
    }
    
    if (selectedModule) {
      filtered = filtered.filter(p => p.module === selectedModule);
    }
    
    return filtered;
  }, [permissions, searchTerm, selectedModule]);

  // Filter users based on search
  const filteredUsers = useMemo(() => {
    if (!userSearchTerm) return users;
    const term = userSearchTerm.toLowerCase();
    return users.filter(u =>
      u.fullName?.toLowerCase().includes(term) ||
      u.username?.toLowerCase().includes(term) ||
      u.email?.toLowerCase().includes(term) ||
      u.designation?.toLowerCase().includes(term)
    );
  }, [users, userSearchTerm]);

  // Check if a permission is assigned to a user
  const isPermissionAssigned = (userId, permissionId) => {
    const perms = localUserPermissions[userId] || [];
    return perms.includes(permissionId);
  };

  // Toggle permission for a user
  const togglePermission = (userId, permissionId) => {
    setLocalUserPermissions(prev => {
      const userPerms = prev[userId] || [];
      const newPerms = userPerms.includes(permissionId)
        ? userPerms.filter(id => id !== permissionId)
        : [...userPerms, permissionId];
      
      return {
        ...prev,
        [userId]: newPerms,
      };
    });
    setHasChanges(true);
  };

  // Save all changes
  const handleSave = async () => {
    if (!hasChanges) return;

    try {
      setSaving(true);
      setError('');

      // Save permissions for each user that has changes
      const savePromises = Object.keys(localUserPermissions).map(async (userIdStr) => {
        const userId = parseInt(userIdStr);
        const originalPerms = userPermissions[userId] || [];
        const newPerms = localUserPermissions[userId] || [];
        
        // Only save if there are changes
        const originalSet = new Set(originalPerms);
        const newSet = new Set(newPerms);
        const changed = originalPerms.length !== newPerms.length ||
          originalPerms.some(id => !newSet.has(id)) ||
          newPerms.some(id => !originalSet.has(id));

        if (changed) {
          await permissionService.setUserPermissions(userId, newPerms);
        }
      });

      await Promise.all(savePromises);
      
      // Reload data without showing loading
      await loadData(false);
      setHasChanges(false);
      toast.success('Permissions updated successfully!');
    } catch (err) {
      const msg = 'Failed to save permissions. Please try again.';
      setError(msg);
      toast.error(msg);
      console.error('Error saving permissions:', err);
    } finally {
      setSaving(false);
    }
  };

  // Reset changes
  const handleReset = () => {
    setLocalUserPermissions({ ...userPermissions });
    setHasChanges(false);
  };

  // Select all permissions for a user in current view
  const selectAllForUser = (userId) => {
    const currentPermIds = filteredPermissions.map(p => p.permissionId);
    const userPerms = localUserPermissions[userId] || [];
    const newPerms = [...new Set([...userPerms, ...currentPermIds])];
    
    setLocalUserPermissions(prev => ({
      ...prev,
      [userId]: newPerms,
    }));
    setHasChanges(true);
  };

  // Deselect all permissions for a user in current view
  const deselectAllForUser = (userId) => {
    const currentPermIds = new Set(filteredPermissions.map(p => p.permissionId));
    const userPerms = localUserPermissions[userId] || [];
    const newPerms = userPerms.filter(id => !currentPermIds.has(id));
    
    setLocalUserPermissions(prev => ({
      ...prev,
      [userId]: newPerms,
    }));
    setHasChanges(true);
  };

  const selectedUser = users.find(u => u.userId === selectedUserId);

  return (
    <div className="permissions-page">
      <div className="page-header">
        <div className="header-content">
          <h2>Permissions Management</h2>
          <p>Manage user permissions and access control</p>
        </div>
        <div className="header-actions">
          {hasChanges && (
            <>
              <button className="btn-secondary" onClick={handleReset} disabled={saving}>
                <HiOutlineArrowPath className="btn-icon" />
                Reset
              </button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="permissions-container">
        {/* Left Sidebar - Users */}
        <div className="roles-sidebar">
          <h3>Users</h3>
          <div className="sidebar-search">
            <input
              type="text"
              placeholder="Search users..."
              value={userSearchTerm}
              onChange={(e) => setUserSearchTerm(e.target.value)}
              className="sidebar-search-input"
            />
          </div>
          {loading && users.length === 0 ? (
            <PageLoading variant="compact" message="Loading users..." />
          ) : filteredUsers.length === 0 ? (
            <div className="empty-state">
              <p>No users found</p>
            </div>
          ) : (
            <div className="roles-list">
              {filteredUsers.map(user => (
                <div
                  key={user.userId}
                  className={`role-item ${selectedUserId === user.userId ? 'active' : ''}`}
                  onClick={() => setSelectedUserId(user.userId)}
                >
                  <div className="user-item-header">
                    <HiOutlineUserCircle className="user-avatar-icon" />
                    <div className="user-item-info">
                      <div className="role-name">{user.fullName}</div>
                      <div className="role-description">{user.designation || user.username}</div>
                    </div>
                  </div>
                  <div className="role-permission-count">
                    {(localUserPermissions[user.userId] || []).length} permissions
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Main Content - Permission Matrix */}
        <div className="permissions-main">
          {/* Filters */}
          <div className="filters-section">
            <div className="search-box">
              <HiOutlineMagnifyingGlass className="search-icon" />
              <input
                type="text"
                placeholder="Search permissions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="module-filter">
              <select
                value={selectedModule || ''}
                onChange={(e) => setSelectedModule(e.target.value || null)}
                className="module-select"
              >
                <option value="">All Modules</option>
                {modules.map(module => (
                  <option key={module} value={module}>{module}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Permission Matrix */}
          {loading && permissions.length === 0 ? (
            <PageLoading message="Loading permissions..." />
          ) : selectedUserId ? (
            <div className="permission-matrix">
              <div className="matrix-header">
                <h3>
                  {selectedUser?.fullName} - Permissions
                  {hasChanges && <span className="unsaved-indicator"> (Unsaved changes)</span>}
                </h3>
                <div className="matrix-actions">
                  <button
                    className="btn-link"
                    onClick={() => selectAllForUser(selectedUserId)}
                  >
                    Select All (Current View)
                  </button>
                  <button
                    className="btn-link"
                    onClick={() => deselectAllForUser(selectedUserId)}
                  >
                    Deselect All (Current View)
                  </button>
                </div>
              </div>

              {/* Group by Module */}
              {modules.map(module => {
                const modulePerms = filteredPermissions.filter(p => p.module === module);
                if (modulePerms.length === 0) return null;

                return (
                  <div key={module} className="permission-module-group">
                    <div className="module-header">
                      <h4>{module}</h4>
                      <span className="module-count">{modulePerms.length} permissions</span>
                    </div>
                    <div className="permissions-grid">
                      {modulePerms.map(permission => {
                        const isAssigned = isPermissionAssigned(selectedUserId, permission.permissionId);
                        return (
                          <div
                            key={permission.permissionId}
                            className={`permission-item ${isAssigned ? 'assigned' : ''}`}
                            onClick={() => togglePermission(selectedUserId, permission.permissionId)}
                          >
                            <div className="permission-checkbox">
                              {isAssigned ? (
                                <HiOutlineCheckCircle className="check-icon" />
                              ) : (
                                <div className="check-empty" />
                              )}
                            </div>
                            <div className="permission-info">
                              <div className="permission-name">{permission.permissionName}</div>
                              <div className="permission-code">{permission.permissionCode}</div>
                              {permission.description && (
                                <div className="permission-description">{permission.description}</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {filteredPermissions.length === 0 && (
                <div className="empty-state">
                  <p>No permissions found matching your search criteria.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="empty-state">
              <HiOutlineLockClosed className="empty-icon" />
              <h3>Select a User</h3>
              <p>Please select a user from the sidebar to manage their permissions.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PermissionsPage;
